import React from 'react';
import * as yup from 'yup';
import { ObjectShape } from 'yup/lib/object';
import { Select, TextField } from '@a-ui/react';
import CheckGroup from '../../components/CheckGroup';
import Datepicker from '../../components/Datepicker';
import RadioGroup from '../../components/RadioGroup';
import { translate } from '../../i18n';
import { useAuthentication } from '../../state/Authentication';

const getVisibleAttributes = (schema: Api.Domain, user: Api.User) => {
  if (!schema) return [];

  return schema.attributes.filter((attribute) => {
    if (!attribute.visibleWhenEmpty) {
      return !!user.domains?.[schema.key]?.attributes?.[attribute.key];
    }

    return true;
  });
};

export const useUserDomains = (schema: Api.Domain[]) => {
  const {
    state: { user },
  } = useAuthentication();

  return schema
    .filter((domain) => Object.keys(user.domains || {}).includes(domain.key))
    .map((domain) => {
      return {
        visibleAttributes: getVisibleAttributes(domain, user),
        schema: domain,
      };
    });
};

export const useDomainSchema = (schema: Api.Domain) => {
  const {
    state: { user },
  } = useAuthentication();

  const visibleAttributes = React.useMemo(() => getVisibleAttributes(schema, user), [schema, user]);

  const data = React.useMemo(() => {
    if (!visibleAttributes?.length) return {};

    return visibleAttributes.reduce((result, attribute) => {
      const value = user.domains?.[schema.key]?.attributes?.[attribute.key];
      if (!value) return result;

      return {
        ...result,
        [attribute.key]: value,
      };
    }, {} as Api.UserDomain['attributes']);
  }, [visibleAttributes, user]);

  const humanReadableData = React.useMemo(() => {
    return visibleAttributes.reduce((result, attribute) => {
      let value: any = user.domains?.[schema.key]?.attributes?.[attribute.key];

      if (attribute.options?.items) {
        const array = [value].flat();

        value = array
          .map((v) => {
            const attr = attribute.options!.items.find((item) => item.value === v);
            return attr?.label || v;
          })
          .join(', ');
      }

      return {
        ...result,
        [attribute.label]: value,
      };
    }, {} as Record<string, string | number>);
  }, [visibleAttributes, user]);

  const renderForm = React.useCallback(() => {
    if (!schema) return undefined;

    return (
      <>
        {visibleAttributes.map((attribute) => {
          const props = {
            key: `${schema.key}-${attribute.key}`,
            label: attribute.label,
            name: attribute.key,
            classNames: 'u-margin-bottom',
            description: attribute.description,
            required: attribute.required,
            disabled: attribute.readonly,
          };

          if (attribute.options && attribute.options.multiple) {
            return <CheckGroup options={attribute.options.items} {...props} />;
          }

          if (attribute.options && attribute.options.display === 'dropdown') {
            return <Select options={attribute.options.items} {...props} />;
          }

          if (attribute.options && attribute.options.display === 'list') {
            return <RadioGroup options={attribute.options.items} {...props} />;
          }

          if (attribute.type === 'date') {
            return <Datepicker {...props} />;
          }

          return <TextField {...props} />;
        })}
      </>
    );
  }, [visibleAttributes]);

  const validationSchema = React.useMemo(() => {
    if (!visibleAttributes.length) return undefined;

    const s = visibleAttributes.reduce((result, attribute) => {
      let validation: yup.AnySchema = yup.string();

      if (attribute.type === 'string') {
        if (attribute.minLength)
          validation = (validation as yup.StringSchema).min(
            attribute.minLength,
            translate('error.input.minLength'),
          );
        if (attribute.maxLength)
          validation = (validation as yup.StringSchema).max(
            attribute.maxLength,
            translate('error.input.maxLength'),
          );
        if (attribute.regex)
          validation = (validation as yup.StringSchema).matches(
            new RegExp(attribute.regex.pattern),
            attribute.regex.error,
          );
      } else if (attribute.type === undefined && attribute.options) {
        const values = attribute.options.items.map((item) => item.value);

        if (attribute.options.multiple) {
          validation = yup.array().of(yup.mixed().oneOf(values));
        } else {
          validation = validation.oneOf([null, ...values]);
        }
      } else if (attribute.type === 'number') {
        validation = yup[attribute.type]();
      }

      if (attribute.required) validation = validation.required('error.input.required');

      return {
        ...result,
        [attribute.key]: validation,
      };
    }, {} as ObjectShape);

    return yup.object().shape(s);
  }, [visibleAttributes]);

  return {
    data,
    humanReadableData,
    schema,
    renderForm,
    validationSchema,
  };
};
